import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Server, CheckCircle, AlertTriangle, RefreshCw, Loader2, Activity } from 'lucide-react';
import {
  getPlatformHealth,
  type ServiceHealth,
} from '../../services/platformAnalyticsService';

export const SystemPage: React.FC = () => {
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  const load = () => {
    setLoading(true);
    getPlatformHealth()
      .then((d) => {
        setServices(d.services || []);
        setError(null);
        setCheckedAt(new Date());
      })
      .catch((e) => setError(e?.response?.data?.message || 'Could not load platform health.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 30_000); // live-ish: re-check every 30s
    return () => clearInterval(id);
  }, []);

  const upCount = services.filter((s) => s.status === 'up').length;
  const allUp = services.length > 0 && upCount === services.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Server className="h-6 w-6 text-orange-500" />
          <div>
            <h1 className="text-2xl font-bold">System</h1>
            <p className="text-sm text-muted-foreground">
              Live health of the platform services.
              {checkedAt ? ` Last checked ${checkedAt.toLocaleTimeString()}.` : ''}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Overall banner */}
      <Card>
        <CardContent className="flex items-center gap-3 p-5">
          {allUp ? (
            <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
          ) : (
            <AlertTriangle className="h-6 w-6 text-amber-500" />
          )}
          <div>
            <div className="font-semibold">
              {services.length === 0
                ? 'Checking services…'
                : allUp
                  ? 'All systems operational'
                  : `${services.length - upCount} service(s) degraded`}
            </div>
            <div className="text-sm text-muted-foreground">
              {upCount}/{services.length} services responding
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Per-service health */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4" /> Services
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {services.map((s) => (
              <div
                key={s.name}
                className="flex items-center justify-between rounded-lg border px-4 py-3"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block h-2.5 w-2.5 rounded-full ${
                      s.status === 'up' ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                  <span className="font-medium">{s.name}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-muted-foreground">{s.responseMs} ms</span>
                  <span
                    className={
                      s.status === 'up'
                        ? 'font-medium text-green-600 dark:text-green-400'
                        : 'font-medium text-red-600 dark:text-red-400'
                    }
                  >
                    {s.status.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
            {!loading && services.length === 0 && (
              <p className="col-span-full py-6 text-center text-sm text-muted-foreground">
                No services reported.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemPage;
